import { Component, inject, signal } from '@angular/core';
import { DialogActionsComponent, DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  CapitalizePipe,
  Client,
  ClientDeliveryAddress,
  ClientDeliveryAddressService,
  ClientInvoiceDraft,
  ClientInvoiceDraftItem,
  ClientInvoiceDraftItemService,
  ClientInvoiceDraftService,
  ClientService,
  errorToast,
  FormErrorService,
  successSaveToast,
  SupplierInvoice,
  SupplierInvoiceItem,
  SupplierInvoiceSection,
  ValidationDirective,
  DropdownLoaderDirective,
} from 'shared';
import { AsyncPipe, UpperCasePipe } from '@angular/common';
import { ComboBoxComponent, DropDownFilterSettings, FilterDirective } from '@progress/kendo-angular-dropdowns';
import { FormFieldComponent } from '@progress/kendo-angular-inputs';
import { LabelComponent } from '@progress/kendo-angular-label';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { catchError, debounceTime, Observable, of, startWith, Subject, switchMap, take, tap, throwError } from 'rxjs';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { NotificationService } from '@progress/kendo-angular-notification';
import { distinctUntilChanged } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'rc-client-invoice-draft-create-modal',
  imports: [
    AsyncPipe,
    CapitalizePipe,
    ComboBoxComponent,
    FormFieldComponent,
    FormsModule,
    LabelComponent,
    ReactiveFormsModule,
    TranslateModule,
    ValidationDirective,
    FilterDirective,
    ButtonComponent,
    DialogActionsComponent,
    UpperCasePipe,
    DropdownLoaderDirective,
  ],
  providers: [FormErrorService, TranslatePipe],
  templateUrl: './client-invoice-draft-create-modal.component.html',
  styleUrl: './client-invoice-draft-create-modal.component.scss',
})
export class ClientInvoiceDraftCreateModalComponent extends DialogContentBase {
  #clientService = inject(ClientService);
  #clientDeliveryAddressService = inject(ClientDeliveryAddressService);
  #clientInvoiceDraftService = inject(ClientInvoiceDraftService);
  #clientInvoiceDraftItemService = inject(ClientInvoiceDraftItemService);
  #filterSubject = new Subject<string>();
  #translateService = inject(TranslateService);
  #notificationService = inject(NotificationService);

  supplierInvoice = signal<SupplierInvoice | undefined>(undefined);
  supplierInvoiceSection = signal<SupplierInvoiceSection | undefined>(undefined);
  supplierInvoiceItem = signal<SupplierInvoiceItem | undefined>(undefined);
  createEmptyDraft = signal<boolean>(false);

  clientInvoiceDraftForm: FormGroup<ClientInvoiceDraftForm> = this.#initForm();

  clients$ = this.#clientService.getClients();
  clientDeliveryAddresses$: Observable<ClientDeliveryAddress[]> =
    this.clientInvoiceDraftForm.controls.client.valueChanges.pipe(
      startWith(this.clientInvoiceDraftForm.controls.client.value),
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => {
        this.clientInvoiceDraftForm.controls.clientDeliveryAddress.reset('');
      }),
      switchMap((clientValue) => {
        const clientIri = clientValue;
        return clientIri
          ? this.#clientDeliveryAddressService.getClientDeliveryAddressesByClientIri(clientIri, true)
          : of([]);
      }),
      tap((addresses) => {
        const defaultAddress = addresses.find((a) => a.default);
        if (defaultAddress) {
          this.clientInvoiceDraftForm.patchValue({ clientDeliveryAddress: defaultAddress['@id'] });
        }
      }),
      takeUntilDestroyed(),
    );

  filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains',
  };

  constructor() {
    const dialog = inject(DialogRef);

    super(dialog);
    this.#filterSubject.pipe(debounceTime(300)).subscribe((value) => {
      this.getClients(value);
    });
  }

  #initForm() {
    const form = new FormGroup<ClientInvoiceDraftForm>({
      client: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      clientDeliveryAddress: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });
    form.updateValueAndValidity();
    return form;
  }

  close() {
    this.dialog.close();
  }

  getClients(value: string) {
    this.clients$ = this.#clientService.findClients(`name=${value}`);
  }

  getData(entries: Client[] | ClientDeliveryAddress[] | null) {
    if (entries) {
      return entries;
    }
    return [];
  }

  onSearch(value: string) {
    this.#filterSubject.next(value);
  }

  onSubmit() {
    const supplierInvoiceSectionId = this.supplierInvoiceSection()!['@id']!;

    const draftPayload = {
      client: this.clientInvoiceDraftForm.value.client,
      clientDeliveryAddress: this.clientInvoiceDraftForm.value.clientDeliveryAddress,
      supplierInvoiceSection: supplierInvoiceSectionId,
    };

    const ensureDraft$ = this.createEmptyDraft()
      ? this.#clientInvoiceDraftService.getForSupplierInvoiceSection(supplierInvoiceSectionId).pipe(
          catchError((err) => {
            if (err?.status === 404) {
              return of(null);
            }
            return throwError(() => err);
          }),
          switchMap((existingDraft) =>
            existingDraft
              ? of(existingDraft)
              : this.#clientInvoiceDraftService.create(draftPayload as unknown as ClientInvoiceDraft),
          ),
        )
      : of(null);

    ensureDraft$
      .pipe(
        //@ts-ignore
        switchMap(() => {
          if (this.supplierInvoiceItem()) {
            return this.#clientInvoiceDraftItemService.createDraftFromSupplierInvoiceSection(
              supplierInvoiceSectionId,
              this.supplierInvoiceItem()!['@id']!,
            );
          } else {
            return this.#clientInvoiceDraftItemService.createFromSupplierInvoiceSection(
              supplierInvoiceSectionId,
              draftPayload as unknown as ClientInvoiceDraftItem,
            );
          }
        }),
        take(1),
      )
      //@ts-ignore
      .subscribe({
        next: () => {
          const n = this.#notificationService.show(successSaveToast);
          const entity = this.supplierInvoiceItem() ? 'CLIENT_INVOICE_DRAFT_ITEM' : 'CLIENT_INVOICE_DRAFT_ITEMS';
          if (n.content) {
            n.content.instance.value = this.#translateService.instant(entity);
            n.content.instance.plural = !this.supplierInvoiceItem();
          }
          this.dialog.close({ isSuccessFull: true });
        },
        error: (err: HttpErrorResponse) => {
          const n = this.#notificationService.show(errorToast);
          if (n.content) n.content.instance.setCurrentValue(err);
        },
      });
  }
}

interface ClientInvoiceDraftForm {
  client: FormControl<string>;
  clientDeliveryAddress: FormControl<string>;
}
